/** Thin wrappers around the browser's capture APIs, with friendly error text. */

export interface DeviceOption {
  deviceId: string
  label: string
}

export async function listDevices(): Promise<{ cameras: DeviceOption[]; mics: DeviceOption[] }> {
  const all = await navigator.mediaDevices.enumerateDevices()
  const map = (kind: MediaDeviceKind, fallback: string) =>
    all
      .filter((d) => d.kind === kind)
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${fallback} ${i + 1}` }))
  return {
    cameras: map('videoinput', 'Camera'),
    mics: map('audioinput', 'Microphone'),
  }
}

export async function getCamera(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
      facingMode: 'user',
    },
    audio: false,
  })
}

export async function getMic(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  })
}

export async function getScreen(wantSystemAudio: boolean): Promise<MediaStream> {
  // `selfBrowserSurface: exclude` stops the Bloom tab itself from showing up in
  // the picker, which otherwise creates an infinite hall-of-mirrors preview.
  const constraints = {
    video: { frameRate: { ideal: 30, max: 60 }, cursor: 'always' },
    audio: wantSystemAudio,
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
    systemAudio: wantSystemAudio ? 'include' : 'exclude',
  } as unknown as DisplayMediaStreamOptions
  return navigator.mediaDevices.getDisplayMedia(constraints)
}

export function stopStream(stream?: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop())
}

export function describeMediaError(err: unknown): string {
  const e = err as { name?: string; message?: string }
  switch (e?.name) {
    case 'NotAllowedError':
      return 'Permission was denied. Bloom needs access to continue — check the padlock icon in the address bar and allow it.'
    case 'NotFoundError':
      return 'No matching device was found. Is your camera or microphone plugged in?'
    case 'NotReadableError':
      return 'That device is already in use by another app. Close it (Zoom, Meet, Photo Booth…) and try again.'
    case 'OverconstrainedError':
      return 'That device could not be used with the requested settings. Try a different one.'
    case 'AbortError':
      return 'The capture was cancelled.'
    default:
      return e?.message || 'Something went wrong while starting capture.'
  }
}
